/**
 * `<SearchBar>` — the query input + retrieval-mode toggle (frontend.md §2.1).
 *
 * UX-reconciliation rip-and-replace: ported VERBATIM from the standalone
 * `docs/refactor/_extracted-standalone-source/05_2cdfc807.jsx` query-bar block —
 * `.se-query` row with a `.searchbox` (icon + input + clear-X button), then a
 * `.seg` segmented mode control, then a `.btn .btn--primary` Search button.
 *
 * Keyboard-first: submit on Enter; clear via the inline X icon. The mode toggle
 * picks hybrid / lexical / semantic. The query + mode are URL state
 * (frontend.md §2.2), so this is a controlled component driven by the surface.
 */

import { useState } from 'react';
import { Icon } from '@/components';
import type { SearchMode } from '@/hooks';

interface SearchBarProps {
  query: string;
  mode: SearchMode;
  onSubmit: (query: string) => void;
  onModeChange: (mode: SearchMode) => void;
}

const MODES: { id: SearchMode; label: string }[] = [
  { id: 'hybrid', label: 'Hybrid' },
  { id: 'lexical', label: 'Lexical' },
  { id: 'semantic', label: 'Semantic' },
];

export function SearchBar({
  query,
  mode,
  onSubmit,
  onModeChange,
}: SearchBarProps): JSX.Element {
  // Local draft so typing does not thrash the URL on every keystroke — committed on submit.
  const [draft, setDraft] = useState(query);

  return (
    <form
      className="se-query"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(draft.trim());
      }}
    >
      <div className="searchbox">
        <Icon name="search" size={16} />
        <input
          type="search"
          placeholder="Ask anything across your corpus…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-label="Search query"
          autoFocus
        />
        {draft && (
          <button
            type="button"
            onClick={() => {
              setDraft('');
              onSubmit('');
            }}
            title="Clear"
            aria-label="Clear query"
          >
            <Icon name="x" size={14} />
          </button>
        )}
      </div>
      <div className="seg" role="radiogroup" aria-label="Retrieval mode">
        {MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            role="radio"
            aria-checked={mode === m.id}
            className={mode === m.id ? 'on' : ''}
            onClick={() => onModeChange(m.id)}
          >
            {m.label}
          </button>
        ))}
      </div>
      <button type="submit" className="btn btn--primary">
        <Icon name="search" size={13} />
        Search
      </button>
    </form>
  );
}
